import { CAP, can, canAny } from '@/lib/can'
import type { CurrentUser } from '@/types/domain'

/* The case page's sections, and how a `?section=` key reaches one (S-11, and
 * its revision after the owner's review of 2026-10-16).
 *
 * SIX SECTIONS. «Обзор» is the summary, «Плечи» the legs, «Команда» the
 * colleagues on the case, «Счета клиенту» what the client is billed and has
 * paid (and Finance's invoicing under it), «Затраты» the costs and what each
 * supplier is owed, «Документы» the paper and the files. The history lives
 * behind the header's «⋯», as on the quote (S-3).
 *
 * THE KEYS ARE THE CONTRACT, the labels are not. `files` is labelled
 * «Документы», and a label and a key are allowed to disagree for the reason
 * they always were — a URL pasted into a chat thread must not break because a
 * word moved. `legs` is a real key again: it opened the overview's legs block
 * while the legs lived there.
 *
 * THE OLD KEYS ARE PERMANENT ALIASES, resolved on read and never written back:
 * a link a colleague pasted months ago still lands where its section went.
 * The app's own invoice page and document row action keep writing `documents`
 * on purpose, because an alias carries an ANCHOR a key does not — it lands on
 * the invoices block rather than at the top of «Счета клиенту».
 *
 * Pure, so it is tested row by row (`caseSections.test.ts`) and the page only
 * renders what it answers. */

export const CASE_SECTION_KEYS = ['overview', 'legs', 'team', 'billing', 'costs', 'files'] as const

export type CaseSectionKey = (typeof CASE_SECTION_KEYS)[number]

/** The two money sections: absent together on a case whose money is
 *  forbidden, exactly where «Деньги» was. */
const MONEY_SECTIONS: readonly CaseSectionKey[] = ['billing', 'costs']

/** A block inside a section that an alias lands on: the invoicing under
 *  «Счета клиенту». Also the block's DOM id. */
export type CaseSectionAnchor = 'case-invoices'

export type CaseSectionResolution =
  /** The session or the record is still loading: nothing is decided, and the
   *  page renders no section body rather than one it would have to replace. */
  | { kind: 'pending' }
  | {
      kind: 'section'
      key: CaseSectionKey
      anchor?: CaseSectionAnchor
      /** The history sheet opens over the section (`?section=history`). */
      openHistory?: boolean
    }

/** Whether this reader is given FINANCE'S WORKSPACE on this case — the rate,
 *  the payment schedule and the invoices and debit notes it raises
 *  (`CaseDocuments`), under «Счета и дебет-ноты» in «Счета клиенту».
 *
 *  The ONE statement of the rule; the page, the section bodies and the resolver
 *  all ask it here. Either document render key opens it — never a role name.
 *  A case whose money this reader may not reach (`moneyUnavailable:
 *  "forbidden"`, story B3 fix round 1) never gives it, whatever the keys: the
 *  workspace reads the case's invoices, debit notes and money, and the server
 *  answers 404 on every one of them for that case. */
export function givenFinanceWorkspace(
  user: CurrentUser | null | undefined,
  moneyForbidden: boolean,
): boolean {
  return !moneyForbidden && canAny(user, CAP.documentInvoiceRender, CAP.documentDebitNoteRender)
}

/** Whether «Документы» opens with the read-only list of the case's paper
 *  (`CaseArchiveList`, F2): a holder of `document.archive.read` who is not
 *  given the workspace — Sales, Pricing and Operations as seeded, and Finance on
 *  a case whose money it may not reach. The workspace already shows the same
 *  paper with its amounts, so a reader given it is not shown it twice. */
export function readsCaseArchive(
  user: CurrentUser | null | undefined,
  moneyForbidden: boolean,
): boolean {
  return !givenFinanceWorkspace(user, moneyForbidden) && can(user, CAP.documentArchiveRead)
}

/** The sections the rail offers, in order. «Счета клиенту» and «Затраты» are
 *  absent on a case whose money is forbidden — they would be nothing but
 *  refusals — and every other section is offered to everybody: «Плечи» as the
 *  legs block always was, «Документы» because it always holds the files. */
export function offeredCaseSections(moneyForbidden: boolean): CaseSectionKey[] {
  return CASE_SECTION_KEYS.filter((key) => !moneyForbidden || !MONEY_SECTIONS.includes(key))
}

/** Which section a `?section=` value opens, and what it reveals there.
 *
 *  The alias table:
 *
 *    · `money`        → «Затраты» (the money block's half that kept its table);
 *    · `documents`    → «Счета клиенту» revealing the invoices block for a
 *                        reader given the workspace, «Документы» for an
 *                        archive-key holder without it, «Обзор» for anyone
 *                        else;
 *    · `participants` → «Команда» (the case has had no participants since
 *                        S-22a; the key survives for links already pasted
 *                        into a chat);
 *    · `history`      → «Обзор» with the history sheet open.
 *
 *  An unknown key and a key this reader is not offered are the same request:
 *  both open «Обзор».
 *
 *  A loading session (`user` undefined) or a loading record answers
 *  `pending`: `documents` depends on both, and an answer given early would
 *  open the wrong section for a frame. A `null` session is an answer — nobody
 *  is signed in — and holds no keys. */
export function resolveCaseSection(
  requested: string | null,
  {
    user,
    moneyForbidden,
    recordLoaded,
  }: {
    user: CurrentUser | null | undefined
    moneyForbidden: boolean
    recordLoaded: boolean
  },
): CaseSectionResolution {
  if (user === undefined || !recordLoaded) return { kind: 'pending' }

  const offered = offeredCaseSections(moneyForbidden)
  const section = (
    key: CaseSectionKey,
    extra: { anchor?: CaseSectionAnchor; openHistory?: boolean } = {},
  ): CaseSectionResolution =>
    offered.includes(key) ? { kind: 'section', key, ...extra } : { kind: 'section', key: 'overview' }

  switch (requested) {
    case 'overview':
    case 'legs':
    case 'team':
    case 'billing':
    case 'costs':
    case 'files':
      return section(requested)
    case 'money':
      return section('costs')
    case 'participants':
      return section('team')
    case 'history':
      return section('overview', { openHistory: true })
    case 'documents':
      if (givenFinanceWorkspace(user, moneyForbidden)) {
        return section('billing', { anchor: 'case-invoices' })
      }
      return readsCaseArchive(user, moneyForbidden) ? section('files') : section('overview')
    default:
      return section('overview')
  }
}
